<template>
  <div class="pa-4">
    <div v-if="loading" class="text-center">
      <h3>{{ $t('message.checking_connection') }}...</h3>
      <circles-loader></circles-loader>
    </div>
    <div v-else class="text-center">
      <div v-if="!loading">
        <v-card-actions class="pa-0 ma-0">
          <v-spacer></v-spacer>
          <v-btn v-if="!isSmallerDisplay()" elevation="0" small color="primary" outlined @click="goToSellAmount()">{{
            $t("message.custom_amount") }}</v-btn>
          <v-btn v-else elevation="0" small fab color="primary" outlined @click="goToSellAmount()">
            <v-icon>mdi-hand-coin-outline</v-icon>
          </v-btn>
        </v-card-actions>
        <div class="supporting-text">{{ $t('message.bulk_goods_only') }}</div>
        <v-card elevation="0" outlined v-if="popularItems" class="pa-3">
          <div class="text-subtitle-2 text-left pa-1 gray--text">{{ $t('message.popular_items') }}</div>
          <v-card-text class="text-center pa-1">
            <template v-for="(i, index) in popularItems">
              <v-badge v-if="i.taxBehavior" :key="'pi_' + index" bordered color="error" icon="mdi-label-percent-outline"
                overlap>
                <v-btn @click="addPopularToCart(i)" x-large elevation="0" class="ml-1" fab>
                  <v-avatar size="60">
                    <ImageDisplay :src="i.image" />
                  </v-avatar>
                </v-btn>
              </v-badge>
              <v-btn v-else @click="addPopularToCart(i)" x-large elevation="0" :key="'pi_' + index" fab class="ml-1">
                <v-avatar size="60">
                  <ImageDisplay :src="i.image" />
                </v-avatar>
              </v-btn>
            </template>
          </v-card-text>
        </v-card>
        <v-card class="text-left mt-2" elevation="0" outlined>
          <div class="pa-2 pb-0">
            <v-text-field outlined dense :label="$t('message.filter_products')" v-model="filterProductsValue" clearable>
            </v-text-field>
          </div>
          <v-skeleton-loader v-if="loadingProducts" type="list-item-avatar-two-line, list-item-avatar-two-line">
          </v-skeleton-loader>
          <v-list-group v-for="(category, index1) in filteredCategories" :key="'category_' + index1" :value="false">
            <template v-slot:activator v-if="categorizedGoods[category].length > 0">
              <v-list-item-title>{{ category == 'uncategorized' ? $t('message.uncategorized') : category }}
                ({{ categorizedGoods[category] ? categorizedGoods[category].length : 0 }})</v-list-item-title>
            </template>
            <v-list-item v-for="(i, index2) in categorizedGoods[category]" :key="'category_item_' + index1 + index2"
              link dense>
              <v-list-item-avatar>
                <v-badge v-if="i.type == 'good' && i.color" bordered bottom size="15" :color="i.color" offset-x="15"
                  offset-y="15">
                  <v-avatar size="35" color="grey lighten-4">
                    <ImageDisplay v-if="i.image" :src="i.image" />
                    <img v-else :src="assets('logo-circular-watermarked.png')" />
                  </v-avatar>
                </v-badge>
                <v-avatar v-else size="35" color="grey lighten-4">
                  <ImageDisplay v-if="i.image" :src="i.image" />
                  <img v-else :src="assets('logo-circular-watermarked.png')" />
                </v-avatar>
              </v-list-item-avatar>
              <v-list-item-content>
                <v-list-item-title class="mb-1 ml-1 mt-1" style="font-size: 14px;">
                  <span class="mr-1 text-subtitle-1">{{ currencySymbol + i.amountDecimal }}</span>
                  <span class="gray--text">{{ i.name }}</span>
                </v-list-item-title>
                <v-list-item-title>
                  <span v-if="i.size">{{ $t('message.size') }}: {{ i.size }}</span>
                  <span v-if="i.availableQuantity != null && !i.unlimited"
                    :style="'font-weight:bold;color:' + getInventoryColor(i.availableQuantity) + ';margin-left:5px'">({{
                      i.availableQuantity }}
                    {{ $t('message.available') }})</span>
                  <v-tooltip v-if="i.taxBehavior" bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon v-bind="attrs" v-on="on" size="20" color="accent">mdi-label-percent-outline</v-icon>
                    </template>
                    <span>{{ $t("message.taxes_enabled") }}</span>
                  </v-tooltip>
                  <v-tooltip v-if="i.altStripeAccount" bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon v-bind="attrs" v-on="on" size="20" color="accent">mdi-bank-transfer</v-icon>
                    </template>
                    <span>{{ $t("message.alternative_stripe_account") }}</span>
                  </v-tooltip>
                </v-list-item-title>
              </v-list-item-content>
              <v-list-item-action>
                <v-checkbox
                  v-if="i.unlimited || i.availableQuantity == null || (i.availableQuantity != null && i.availableQuantity != 0)"
                  v-model="cart" :value="i"></v-checkbox>
              </v-list-item-action>
            </v-list-item>
          </v-list-group>
        </v-card>
        <v-card class="mt-2" elevation="0" outlined>
          <v-card-title style="font-size: 16px;">
            <span class="mr-2">
              <v-icon>mdi-cart-outline</v-icon>
            </span>
            {{ $t('message.shopping_cart') }} ({{ cart.length }}) <span v-if="isAppAdmin()">(automaticTaxes: {{
              automaticTaxes
              }})</span>
          </v-card-title>
          <v-card-text>
            <v-list dense>
              <template v-for="(i, index) in cart">
                <v-list-item dense :key="'invoice' + index" class="text-left">
                  <v-list-item-avatar>
                    <v-badge v-if="i.type == 'good' && i.color" bordered bottom size="35" :color="i.color" offset-x="35"
                      offset-y="15">
                      <v-avatar size="35" color="grey lighten-4">
                        <ImageDisplay v-if="i.image" :src="i.image" />
                        <img v-else :src="assets('logo-circular-watermarked.png')" />
                      </v-avatar>
                    </v-badge>
                    <v-avatar v-else size="35" color="grey lighten-4">
                      <ImageDisplay v-if="i.image" :src="i.image" />
                      <img v-else :src="assets('logo-circular-watermarked.png')" />
                    </v-avatar>
                  </v-list-item-avatar>
                  <v-list-item-content>
                    <v-list-item-title>
                      {{ i.name }}
                      <span class="title-amount">{{ currencySymbol + i.amountDecimal }}</span>
                    </v-list-item-title>
                  </v-list-item-content>
                  <v-list-item-action>
                    <v-select style="width:90px" :items="i.quantities" outlined dense
                      @change="changeCartItemQuantity(i)" v-model="i.quantity"></v-select>
                  </v-list-item-action>
                </v-list-item>
                <v-divider v-if="index + 1 < cart.length" :key="index"></v-divider>
              </template>
            </v-list>
          </v-card-text>
          <v-card-title style="font-size: 16px;">
            <span class="mr-2">
              <v-icon>mdi-currency-usd</v-icon>
            </span>
            TOTAL: {{ currencySymbol + transactionTotal }}
            <v-tooltip v-if="cartContainsAltStripe" bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-icon class="ml-1" v-bind="attrs" v-on="on" size="20" color="accent">mdi-bank-transfer</v-icon>
              </template>
              <span>{{ $t("message.alternative_stripe_account") }}</span>
            </v-tooltip>
            <span v-if="cartContainsAltStripe && !isSmallerDisplay()" class="ml-1 accent--text text-caption">{{
              $t("message.alternative_stripe_account") }}</span>
          </v-card-title>
        </v-card>
        <v-card class="mt-2" elevation="0" outlined>
          <v-card-text>
            <v-autocomplete hide-details v-model="selectedMember" :items="members" :label="$t('message.pick_member')"
              item-text="text" item-value="value" clearable outlined dense></v-autocomplete>
            <div class="pb-1 pt-1">{{ $t('message.or') }}</div>
            <v-text-field hide-details outlined dense label="Email" v-model="email"></v-text-field>
          </v-card-text>
        </v-card>
        <v-card class="mt-2" elevation="0" outlined v-if="transactionTotal > 0">
          <v-card-text class="pt-0">
            <v-switch hide-details v-model="applyDiscount" :label="$t('message.apply_discount')"></v-switch>
            <v-text-field :disabled="discountAmount > 0" dense outlined v-show="applyDiscount" v-model="discountPercent"
              :label="$t('message.discount_percent')" :rules="[validationRules.number]"></v-text-field>
            <v-text-field :disabled="discountPercent > 0" dense outlined v-show="applyDiscount" v-model="discountAmount"
              :label="$t('message.discount_amount')" :rules="[validationRules.decimal]"></v-text-field>
            <v-card-actions v-show="applyDiscount">
              <v-btn @click="setDiscount(20)" small outlined color="primary">20%</v-btn>
              <v-btn @click="setDiscount(25)" small outlined color="primary">25%</v-btn>
              <v-btn @click="setDiscount(100)" small outlined color="primary">100%</v-btn>
            </v-card-actions>
            <div class="text-left accent--text" v-if="applyDiscount && discountedTotal >= 0">
              {{ $t('message.new_total') }}: <b>{{ currencySymbol + (discountedTotal >= 0 && (discountAmount > 0 ||
                discountPercent > 0) ? discountedTotal : transactionTotal) }}</b></div>
          </v-card-text>
        </v-card>
        <v-card v-show="false" class="mt-2" elevation="0" outlined v-if="stripeReady & selectedMember">
          <v-card-text>
            <v-switch v-model="saveCreditCard" :label="$t('message.save_credit_card')"></v-switch>
          </v-card-text>
        </v-card>
        <v-card class="mt-2" elevation="0" outlined v-if="!checkoutInitialized && transactionTotal > 0">
          <div v-if="handlingCharges" class="text-center">
            <circles-loader></circles-loader>
          </div>
          <v-card-actions v-if="!paymentsReady">
            <PaymentTypeSelect v-model="paidWith"></PaymentTypeSelect>
          </v-card-actions>
          <div v-if="!isSmallerDisplay()">
            <v-card-actions v-allowed="'sell_items'">
              <div v-if="paymentsReady">
                <v-btn outlined small elevation="0" :disabled="!stripeReady || handlingCharges" v-if="transactionTotal > 0 &&
                  discountedTotal != 0 && (email || selectedMember)" color="primary" @click="initCheckout()"
                  class="mr-1">
                  <v-icon>mdi-credit-card-outline</v-icon>
                  {{ $t('message.pay_with_card') }}
                </v-btn>
                <v-btn outlined elevation="0" :disabled="!canUseTerminal || handlingCharges" small v-if="transactionTotal > 0 &&
                  discountedTotal != 0 && (email || selectedMember) && terminalAvailable" color="primary"
                  @click="initTerminalCheckout()" class="mr-1">
                  <v-icon>mdi-calculator</v-icon>
                  {{ $t('message.pay_with_terminal') }}
                </v-btn>
                <v-btn outlined :disabled="handlingCharges" small elevation="0" color="primary" v-if="transactionTotal > 0 &&
                  discountedTotal != 0 && selectedMember && paymentsReady && paymentMethod && !paymentMethod.expired"
                  @click="savedCardPurchase()" class="mr-1">
                  <v-icon v-if="isAchPaymentType(paymentMethod.type)">mdi-bank</v-icon>
                  <v-icon v-else>mdi-credit-card-check-outline</v-icon>
                  <span v-if="isAchPaymentType(paymentMethod.type)">{{ $t('message.pay_saved') }} {{ paymentMethod.type
                    }}</span>
                  <span v-else>{{ $t('message.pay_saved_card') }}</span>
                </v-btn>
                <v-btn outlined small elevation="0" :disabled="!stripeReady || handlingCharges" v-if="transactionTotal > 0 &&
                  discountedTotal != 0 && selectedMember" color="primary" @click="initCheckout(true)" class="mr-1">
                  <v-icon>mdi-cellphone</v-icon>
                  {{ $t('message.process_payment_send') }}
                </v-btn>
              </div>
              <v-btn outlined :disabled="handlingCharges" small elevation="0" v-if="transactionTotal > 0"
                color="primary" @click="payCash()">
                <PaymentType v-if="!paymentsReady" size="20" :type="paidWith"></PaymentType>
                <v-icon v-else>mdi-cash-100</v-icon>
                <span v-if="!paymentsReady">
                  {{ $t('message.paid_with') }} {{ paidWith }}
                </span>
                <span v-else>
                  {{ $t('message.pay_cash') }}
                </span>
              </v-btn>
            </v-card-actions>
            <v-card-actions>
              <div v-if="stripeReady">
                <v-btn outlined small elevation="0" :disabled="!stripeReady || handlingCharges" v-if="!applyDiscount && transactionTotal > 0 &&
                  discountedTotal != 0 && (selectedMember || email)" color="primary" @click="processSaleViaLink(false)"
                  class="mr-1">
                  <v-icon>mdi-content-copy</v-icon>
                  {{ $t('message.copy_checkout_link') }}
                </v-btn>
                <v-btn outlined small elevation="0" :disabled="!stripeReady || handlingCharges" v-if="!applyDiscount && transactionTotal > 0 &&
                  discountedTotal != 0 && (selectedMember || email)" color="primary" @click="processSaleViaLink(true)"
                  class="mr-1"><v-icon>mdi-email-fast-outline</v-icon>
                  {{ $t('message.send_checkout_link') }}
                </v-btn>
              </div>
              <v-btn elevation="0" small @click="cancel()">{{ $t('message.cancel') }}</v-btn>
            </v-card-actions>
          </div>
          <div v-else-if="transactionTotal > 0">
            <div v-if="paymentsReady">
              <v-btn outlined elevation="0" class="mt-2" block :disabled="!stripeReady || handlingCharges" small v-if="transactionTotal > 0 &&
                discountedTotal != 0 && (email || selectedMember)" color="primary" @click="initCheckout()">
                <v-icon>mdi-credit-card-outline</v-icon>
                {{ $t('message.pay_with_card') }}
              </v-btn>
              <v-btn outlined elevation="0" class="mt-2" block :disabled="!stripeReady || handlingCharges" small v-if="(email || selectedMember) &&
                discountedTotal != 0 && terminalAvailable" color="primary" @click="initTerminalCheckout()">
                <v-icon>mdi-calculator</v-icon>
                {{ $t('message.pay_with_terminal') }}
              </v-btn>
              <v-btn outlined :disabled="handlingCharges" class="mt-2" block small elevation="0" color="primary" v-if="transactionTotal > 0 &&
                discountedTotal != 0 && paymentsReady && paymentMethod && !paymentMethod.expired"
                @click="savedCardPurchase()">
                <v-icon v-if="isAchPaymentType(paymentMethod.type)">mdi-bank</v-icon>
                <v-icon v-else>mdi-credit-card-check-outline</v-icon>
                <span v-if="isAchPaymentType(paymentMethod.type)">{{ $t('message.pay_saved') }} {{ paymentMethod.type
                  }}</span>
                <span v-else>{{ $t('message.pay_saved_card') }}</span>
              </v-btn>
              <v-btn outlined elevation="0" class="mt-2" block :disabled="!stripeReady || handlingCharges" small v-if="transactionTotal > 0 &&
                discountedTotal != 0 && selectedMember" color="primary" @click="initCheckout(true)">
                <v-icon>mdi-cellphone</v-icon>
                {{ $t('message.process_payment_send') }}
              </v-btn>
            </div>
            <v-btn outlined :disabled="handlingCharges" elevation="0" small class="mt-2" block
              v-if="transactionTotal > 0" color="primary" @click="payCash()">
              <PaymentType v-if="!paymentsReady" size="20" :type="paidWith"></PaymentType>
              <v-icon v-else>mdi-cash-100</v-icon>
              <span v-if="!paymentsReady">
                {{ $t('message.paid_with') }} {{ paidWith }}
              </span>
              <span v-else>
                {{ $t('message.pay_cash') }}
              </span>
            </v-btn>
            <div v-if="stripeReady">
              <v-btn class="mt-2" block outlined small elevation="0" :disabled="!stripeReady || handlingCharges" v-if="!applyDiscount && transactionTotal > 0 &&
                discountedTotal != 0 && (selectedMember || email)" color="primary" @click="processSaleViaLink(false)">
                <v-icon>mdi-content-copy</v-icon>
                {{ $t('message.copy_checkout_link') }}
              </v-btn>
              <v-btn class="mt-2" block outlined small elevation="0" :disabled="!stripeReady || handlingCharges" v-if="!applyDiscount && transactionTotal > 0 &&
                discountedTotal != 0 && (selectedMember || email)" color="primary" @click="processSaleViaLink(true)">
                <v-icon>mdi-email-fast-outline</v-icon>
                {{ $t('message.send_checkout_link') }}
              </v-btn>
              <v-btn elevation="0" small class="mt-2" block @click="cancel()">{{ $t('message.cancel')
                }}</v-btn>
            </div>
          </div>
        </v-card>
        <v-card v-if="checkoutInitialized" class="mt-2" elevation="0" outlined>
          <v-card-text color="success">
            <div class="text-subtitle-2 mb-1">{{ $t("message.charge_notification") }}</div>
            <v-btn v-if="back" elevation="0" small @click="goBack()">{{
              $t("message.back") }}
            </v-btn>
          </v-card-text>
        </v-card>

        <v-card class="mt-2" elevation="0" outlined v-if="false">
          <v-card-actions color="success">
            <v-btn outlined v-if="back" color="primary" elevation="0" small @click="connectReader()">
              CONNECT reader
            </v-btn>
          </v-card-actions>
        </v-card>
      </div>
      <v-dialog :persistent="true" transition="dialog-bottom-transition" v-model="waitingForReaderPayment"
        max-width="70%">
        <v-card class="pa-0 pt-2" elevation="0" outlined>
          <v-card-text class="pa-0">
            <div class="text-h6 text-center mb-2">{{ $t("message.waiting_terminal_payment") }}</div>
            <div class="text-center">
              <v-progress-linear striped height="50" indeterminate color="success"></v-progress-linear>
            </div>
          </v-card-text>
          <v-card-actions class="mt-2 mb-2">
            <v-spacer></v-spacer>
            <v-btn elevation="0" @click="cancelTerminalTransaction()">{{ $t('message.cancel')
              }}</v-btn>
            <v-spacer></v-spacer>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </div>
  </div>
</template>

<script>
import CirclesLoader from "@/components/displays/CirclesLoader.vue";
import PaymentType from "@/components/displays/PaymentTypeDisplay.vue";
import PaymentTypeSelect from "@/components/inputs/PaymentTypeSelect.vue";
import Api from "@/services/Api";
import Api2 from "@/services/Api2";

export default {
  data() {
    return {
      loading: true,
      checkoutInitialized: false,
      validQuantity: false,
      members: [],
      filteredCategories: [],
      academyImage: null,
      categories: null,
      currency: null,
      filterProductsValue: "",
      terminalStripeAccountId: null,
      mainStripeAccountId: null,
      canUseTerminal: true,
      selectedMember: this.$route.query.userId
        ? this.$route.query.userId
        : null,
      email: this.$route.query.email
        ? this.$route.query.email
        : null,
      paymentsReady: false,
      paymentMethod: null,
      saveCreditCard: true,
      categorizedGoodscategorizedGoods: [],
      categorizedGoods: {},
      cart: [],
      cartContainsAltStripe: false,
      transactionTotal: 0,
      handlingCharges: false,
      loadingProducts: false,
      popularItems: null,
      back: false,
      paymentsModule: null,
      waitingForPayment: false,
      paidWith: null,
      stripeReady: false,
      applyDiscount: false,
      discountPercent: null,
      discountAmount: null,
      discountedTotal: null,
      automaticTaxes: false,
      terminal: null,//Stripe terminal with the data
      terminalAvailable: false,
      terminalLocation: null,
      readerInUse: null,
      waitingForReaderPayment: false,
      readerDisconnected: false,
      currencySymbol: '$'
    };
  },
  components: {
    CirclesLoader,
    PaymentTypeSelect,
    PaymentType
  },
  created() {
    Api2()
      .get(
        "admins/academy/" +
        this.$store.state.managedAcademy +
        "/is_connected/all"
      )
      .then((response) => {
        this.stripeReady = response.data.stripeConnected;
        if (!this.stripeReady)
          this.canUseTerminal = false;
        if (!this.stripeReady && response.data.paymentsModule == 'easypay' && response.data.paymentsReady) {
          this.stripeReady = true;
        }
      });
    Api2()
      .get("admins/academy/" + this.$store.state.selectedAcademy)
      .then((response) => {
        this.currency = response.data.currency;
        if (response.data.currencySymbol)
          this.currencySymbol = response.data.currencySymbol;
        this.paymentsReady =
          response.data.paymentsReady && response.data.stripeAccountId;
        if (!this.paymentsReady)
          this.paidWith = 'cash';
        this.paymentsModule = response.data.paymentsModule;
        if (this.paymentsModule == 'infinicept')
          this.paymentsReady = true;
        else if (this.paymentsModule == 'easypay')
          this.paymentsReady = true;
        if (this.paymentsModule != 'infinicept' && this.paymentsModule != 'easypay' && !this.isSmallerDisplay())
          this.getTerminalAvailability();
        this.mainStripeAccountId = response.data.stripeAccountId;
        this.academyImage = response.data.image;
        this.loading = false;
        this.getProducts();
        Api2()
          .get(
            "admins/academy/" + this.$store.state.selectedAcademy + "/members"
          )
          .then((response) => {
            const data = response.data;
            if (data) {
              for (const m of data.approved) {
                this.members.push({
                  value: m._id,
                  email: m.email,
                  text: m.fName + " " + m.lName,
                });
              }
            }
          })
          .catch((e) => {
            console.log(e)
            if (e)
              this.showAlert(
                "error",
                this.$t("message.error_generic")
              );
          });
      });
  },
  mounted() {
    let stripeTerminalScript = document.createElement('script')
    stripeTerminalScript.setAttribute('src', 'https://js.stripe.com/terminal/v1/')
    document.head.appendChild(stripeTerminalScript);
  },
  watch: {
    filterProductsValue: {
      immediate: false,
      handler: function (newValue) {
        this.fillCategories(newValue);
      },
    },
    discountPercent: {
      handler: function (newValue) {
        if (!isNaN(newValue) && this.transactionTotal > 0)
          this.discountedTotal = this.getDiscountedPrice(this.transactionTotal, newValue)
      },
    },
    discountAmount: {
      handler: function (newValue) {
        if (!isNaN(newValue) && this.transactionTotal > 0)
          this.discountedTotal = this.getDiscountedPrice(this.transactionTotal, newValue, newValue)
      },
    },
    cart: {
      handler: function () {
        this.calculateGrandTotal();
      },
    },
    selectedMember: {
      immediate: true,
      handler: function (newValue) {
        if (newValue) {
          this.getPaymentMethod();
          this.validateMember();
        }
      },
    },
  },
  methods: {
    processSaleViaLink(sendEmail) {
      let email = this.email;
      if (!email) {
        for (const m of this.members) {
          if (m.value == this.selectedMember) {
            email = m.email;
            break;
          }
        }
      }
      if (!email) {
        this.showAlert("error", "Oops!", this.$t("message.email_member_required"));
        return;
      }
      let proceed = false;
      let saleUrl = null;
      if (this.cart && this.cart.length == 1) {
        for (const i of this.cart) {
          const id = i._id;
          if (i.amountDecimal <= 0) {
            proceed = false;
            break;
          }
          this.qrProduct = i;
          const encodedParams = this.encodeBase64("processed_by=desk&email=" + email);
          saleUrl = 'https://core.bjjlink.com/pub/academy/' + this.$store.state.selectedAcademy + '/product/' + id + "?p=" + encodedParams;
          proceed = true;
          break;
        }
      } else {
        const items = this.setupItems(true);
        if (items) {
          let itemsUrl = "";
          for (const i of items) {
            itemsUrl += '&products=' + i.quantity + "|" + i.product
          }
          this.qrProduct = {
            currencySymbol: this.currency,
            amountDecimal: this.cartTotal,
          };
          const encodedParams = this.encodeBase64(itemsUrl + "&processed_by=tablet&email=" + email);
          //const encodedParams = itemsUrl + "&processed_by=tablet&email=" + email;
          saleUrl = 'https://core.bjjlink.com/pub/academy/' + this.$store.state.selectedAcademy + '/product/multiple?p=' + encodedParams;
          proceed = true;
        }
      }
      if (proceed) {
        if (sendEmail) {
          Api()
            .post(
              "admins/managedacademy/" +
              this.$store.state.managedAcademy +
              "/email/products/checkout/link", { email: email, link: saleUrl }
            )
            .then((response) => {
              if (response.data) {
                this.showAlert(
                  "success",
                  this.$t("message.success")
                );
              }
            }).catch((e) => {
              console.log(e)
              this.showAlert("error", this.$t("message.error_generic"));
            });
        } else {
          this.copyToClipboard(saleUrl);
        }
      }
    },
    setDiscount(val) {
      this.discountPercent = val;
    },
    goToSellAmount() {
      this.navigateTo({ name: 'sellamount', query: { userId: this.selectedMember } })
    },
    validateMember() {
      Api2()
        .patch("admins/academy/" + this.$store.state.managedAcademy + "/member/" + this.selectedMember + "/validate");
    },
    addPopularToCart(item) {
      let found = false;
      for (let i of this.cart) {
        if (i._id == item._id) {
          found = true;
          break;
        }
      }
      if (!found) {
        item.quantity = 1;
        this.cart.push(item);
      }
    },
    cancelTerminalTransaction() {
      if (this.readerInUse) {
        const vm = this;
        try {
          this.terminal.cancelCollectPaymentMethod().then(function (result) {
            if (result.error) {
              vm.waitingForReaderPayment = false;
              vm.readerInUse = null;
            } else {
              // Placeholder for processing result.paymentIntent
              vm.waitingForReaderPayment = false;
              vm.readerInUse = null;
            }
          });
        } catch (e) {
          console.log('859: ', e);
          this.waitingForReaderPayment = false;
          this.readerInUse = null;
        }
      } else {
        console.log('NOT IN USE')
      }
    },
    initTerminalCheckout() {
      this.waitingForReaderPayment = false;
      if (this.handlingCharges)
        return;
      if (this.applyDiscount && this.discountedTotal < 0) {
        this.showAlert("error", this.$t("message.incorrect_discount_number"));
        return;
      }
      this.back = false;
      this.handlingCharges = true;
      const items = this.setupItems();
      let discountPercent = null;
      let discountAmount = null;
      if (this.applyDiscount && this.discountPercent) {
        try {
          discountPercent = new Number(this.discountPercent).valueOf();
        } catch (err) {
          this.showAlert("error", this.$t("message.incorrect_discount_number"));
          return;
        }
      } else if (this.applyDiscount && this.discountAmount) {
        try {
          discountAmount = new Number(this.discountAmount).valueOf();
        } catch (err) {
          this.showAlert("error", this.$t("message.incorrect_discount_number"));
          this.handlingCharges = false;
          return;
        }
      }
      const vm = this;
      let postData = {
        items: items,
        processedBy: "terminal",
        discountPercent: discountPercent,
        discountAmount: discountAmount,
        discountedTotal: this.discountedTotal,
        quantity: 1,
        buyer: this.selectedMember,
        email: this.email,
      };
      let postUrl =
        "payments/academy/" +
        this.$store.state.managedAcademy +
        "/terminals/init/payment";
      Api()
        .post(postUrl, postData)
        .then((response) => {
          const clientSecret = response.data.clientSecret;
          postData.customer = response.data.customer;
          postData.paymentIntent = response.data.paymentIntent;

          this.waitingForReaderPayment = true;
          //Now we call the terminal
          this.terminal.collectPaymentMethod(clientSecret).then(function (collectResult) {
            if (collectResult.error) {
              vm.handlingCharges = false;
              vm.waitingForReaderPayment = false;
              if (collectResult.error.code != 'canceled')
                vm.showAlert("error", collectResult.error.message);
            } else {
              // Placeholder for processing result.paymentIntent
              vm.terminal.processPayment(collectResult.paymentIntent).then(function (processPaymentResult) {
                if (processPaymentResult.error) {
                  console.log('681: ', processPaymentResult.error)
                  vm.showAlert("error", processPaymentResult.error.message);
                  vm.handlingCharges = false;
                  vm.waitingForReaderPayment = false;
                  vm.readerInUse = null;
                } else if (processPaymentResult.paymentIntent) {
                  vm.completeTerminalPurchase(postData);
                  vm.waitingForReaderPayment = false;
                  vm.clearCart();
                  vm.handlingCharges = false;
                }
              });
            }
          });
        })
        .catch((e) => {
          this.waitingForReaderPayment = false;
          this.handlingCharges = false;
          console.log('699: ', e)
          if (e)
            this.showAlert("error", this.$t("message.error_generic"));
        });
    },
    completeTerminalPurchase(postData) {
      //Send it to the back end to record our transaction
      Api()
        .post(
          "payments/academy/" +
          this.$store.state.managedAcademy +
          "/terminals/complete/payment", postData
        )
        .then((response) => {
          if (response.data) {
            this.showAlert(
              "success",
              this.$t("message.payment_completed")
            );
          }
        }).catch((e) => {
          console.log('720: ', e)
          this.showAlert("error", this.$t("message.error_generic"));
        });
    },
    getTerminalAvailability() {
      Api2()
        .get(
          "payments/academy/" +
          this.$store.state.managedAcademy +
          "/terminals/"
        )
        .then((response) => {
          if (response.data && response.data.locations && response.data.locations.length > 0) {
            //If we have a connected reader, connect, if not, move on
            this.terminalLocation = response.data.locations[0].id;
            this.terminalStripeAccountId = response.data.locations[0].stripeAccount;
            this.connectToReader();
            //Hide reader if a selected products controls the stripe account id
          }
        });
    },
    refreshReaderConnection() {
      if (this.terminalLocation) {
        this.connectToReader();
      }
    },
    connectToReader() {
      this.readerDisconnected = false;
      this.readerInUse = null;
      const vm = this;
      const loc = this.terminalLocation;
      this.connectingReader = true;
      /*eslint-disable no-undef*/
      this.terminal = StripeTerminal.create({
        onFetchConnectionToken: function () {
          return Api()
            .post("payments/academy/" + vm.$store.state.managedAcademy + "/terminals/connection_token", { location: loc })
            .then((response) => {
              return response.data.secret;
            })
        },
        onUnexpectedReaderDisconnect: function () {
          vm.readerDisconnected = true;
          vm.showAlert("error", this.$t("message.reader_disconnected"));
        },
      });
      const config = { simulated: false, location: loc };
      //const config = { simulated: true };
      this.terminal.discoverReaders(config).then(function (discoverResult) {
        if (discoverResult.error) {
          console.log('770: ', discoverResult.error)
          vm.showAlert("error", discoverResult.error.message);
          vm.connectingReader = false;
        } else if (discoverResult.discoveredReaders.length === 0) {
          vm.showAlert("error", this.$t("message.no_readers_available"));
          vm.connectingReader = false;
        } else {
          //We are only grabbing 1 reader, the id matches the one we should have gotten from the beginning
          const discoveredReader = discoverResult.discoveredReaders[0];
          //We got a reader, lets connect
          vm.terminal.connectReader(discoveredReader).then(function (connectResult) {
            if (connectResult.error) {
              //vm.showAlert("error", vm.$t("message.connection_error"));
              vm.connectingReader = false;
              vm.terminalAvailable = false;
            } else {
              vm.terminalAvailable = true;
              vm.canUseTerminal = true;
              vm.connectingReader = false;
              vm.readerInUse = discoveredReader.id;
            }
          });
        }
      });
    },
    getPaymentMethod() {
      Api2()
        .get(
          "payments/academy/" +
          this.$store.state.managedAcademy +
          "/customer/" +
          this.selectedMember
        )
        .then((response) => {
          if (response.data && response.data.paymentMethod) {
            this.paymentMethod = response.data.paymentMethod;
            if (this.paymentMethod.expYear) {
              let expDate = new Date();
              expDate.setFullYear(this.paymentMethod.expYear);
              expDate.setMonth(this.paymentMethod.expMonth - 1);
              this.paymentMethod.expDate = expDate;
              let today = new Date();
              if (expDate > today) this.paymentMethod.expired = false;
              else this.paymentMethod.expired = true;
            } else {
              //With the new payment we dont know exp date
              this.paymentMethod.expired = false;
            }
          } else {
            this.paymentMethod = null;
          }
        });
    },
    savedCardPurchase() {
      if (this.handlingCharges)
        return;
      this.waitingForPayment = true;
      this.handlingCharges = true;
      const items = this.setupItems();
      let discountPercent = null;
      let discountAmount = null;
      if (this.applyDiscount && this.discountPercent) {
        try {
          discountPercent = new Number(this.discountPercent).valueOf();
        } catch (err) {
          this.showAlert("error", this.$t("message.incorrect_discount_number"));
          return;
        }
      } else if (this.applyDiscount && this.discountAmount) {
        try {
          discountAmount = new Number(this.discountAmount).valueOf();
        } catch (err) {
          this.showAlert("error", this.$t("message.incorrect_discount_number"));
          return;
        }
      }
      const postData = {
        items: items,
        discountPercent: discountPercent,
        discountAmount: discountAmount,
        discountedTotal: this.discountedTotal,
        buyer: this.selectedMember,
      };
      var postUrl =
        "payments/academy/" +
        this.$store.state.managedAcademy +
        "/onetime/savedcard";
      Api()
        .post(postUrl, postData)
        .then((response) => {
          const data = response.data;
          if (data) {
            this.navigateTo({ name: 'viewitemstransaction', params: { transactionId: data.transactionId } });
          }
        })
        .catch((e) => {
          console.log('869: ', e)
          this.handlingCharges = false;
          this.waitingForPayment = false;
          if (e.response.status == 500) {
            const errData = e.response.data;
            this.showAlert(
              "error",
              this.$t("message.error_code") + ": " + errData.type,
              errData.message,
              15000
            );
          } else {
            this.showAlert("error", this.$t("message.error_generic"));
          }
        });
    },
    getAvailableQuantities(i) {
      let total = 10;
      let quantities = [];
      if (i.availableQuantity == null) {
        total = 20;
      } else {
        total = i.availableQuantity;
        if (total > 20)
          total = 20;
      }
      for (let index = 0; index <= total; index++) {
        quantities.push(index);
      }
      return quantities;
    },
    calculateGrandTotal() {
      let grandTotal = 0;
      this.automaticTaxes = false;
      for (const i of this.cart) {
        if (i.taxBehavior)
          this.automaticTaxes = true;
        grandTotal += i.amountDecimal * i.quantity;
        if (i.altStripeAccount) {
          this.cartContainsAltStripe = true;
          if (i.altStripeAccount != this.mainStripeAccountId) {
            this.canUseTerminal = false;
          }
        } else if (this.terminalStripeAccountId && this.mainStripeAccountId != this.terminalStripeAccountId) {
          this.canUseTerminal = false;
        }
      }
      if (this.cart.length == 0)
        this.canUseTerminal = true;
      this.transactionTotal = grandTotal;
    },
    changeCartItemQuantity(i) {
      if (i.quantity == 0) {
        i.quantity = 1;
        this.removeCartItem(i._id);
      }
      this.calculateGrandTotal();
    },
    removeCartItem(id) {
      var index = 0;
      for (const i of this.cart) {
        if (i._id.toString() == id) {
          break;
        }
        index++;
      }
      this.cart.splice(index, 1);
    },
    getPopularItems() {
      Api2()
        .get(
          "admins/academy/" + this.$store.state.managedAcademy + "/products/popular/items"
        )
        .then((response) => {
          const data = response.data;
          if (data) {
            if (data.popularItems && data.popularItems.length > 0) {
              let popularItems = [];
              for (let i of data.popularItems) {
                i.quantities = this.getAvailableQuantities(i);
                popularItems.push(i)
              }
              this.popularItems = popularItems;
            }
          }
        });
    },
    getProducts() {
      this.loadingProducts = true;
      this.getPopularItems();
      Api2()
        .get(
          "admins/academy/" +
          this.$store.state.managedAcademy +
          "/products/get/categories"
        )
        .then((response) => {
          this.categories = response.data;
          this.categories.push("uncategorized");
          Api2()
            .get(
              "admins/academy/" + this.$store.state.managedAcademy + "/products"
            )
            .then((response) => {
              const data = response.data;
              if (data) {
                let goods = [];
                for (let g of data.goods) {
                  if (!g.isMembership) {
                    g.quantities = this.getAvailableQuantities(g);
                    goods.push(g)
                  }
                }
                this.goods = goods;
                this.loadingProducts = false;
                this.fillCategories();
              }
            })
            .catch((e) => {
              this.loadingProducts = false;
              if (e)
                this.showAlert(
                  "error",
                  this.$t("message.error_generic")
                );
            });
        });
    },
    fillCategories(value) {
      if (value) value = value.toLowerCase();
      try {
        if (this.categories && this.categories.length > 0) {
          this.categorizedGoods["uncategorized"] = [];
          for (const c of this.categories) this.categorizedGoods[c] = [];
          //Now proceed to add all the products
          var tmpCategories = [];
          if (this.goods) {
            this.goodsLength = this.goods.length;
            for (const g of this.goods) {
              if (
                !value ||
                value == "" ||
                g.name.toLowerCase().indexOf(value) >= 0
              ) {
                g.quantity = 1;
                if (g.category) {
                  this.categorizedGoods[g.category].push(g);
                  if (tmpCategories.indexOf(g.category) < 0)
                    tmpCategories.push(g.category);
                } else this.categorizedGoods["uncategorized"].push(g);
              }
            }
          }
          this.filteredCategories = tmpCategories.sort();
          if (this.categorizedGoods["uncategorized"].length > 0)
            this.filteredCategories.push("uncategorized");
        }
      } catch (e) {
        console.log('1027: ', e);
      }
    },
    payCash() {
      if (this.handlingCharges)
        return;
      if (this.applyDiscount && this.discountedTotal < 0) {
        this.showAlert("error", this.$t("message.incorrect_discount_number"));
        return;
      }
      this.back = false;
      this.handlingCharges = true;
      const items = this.setupItems(true);
      let discountPercent = null;
      let discountAmount = null;
      if (this.applyDiscount && this.discountPercent) {
        try {
          discountPercent = new Number(this.discountPercent).valueOf();
        } catch (err) {
          this.showAlert("error", this.$t("message.incorrect_discount_number"));
          return;
        }
      } else if (this.applyDiscount && this.discountAmount) {
        try {
          discountAmount = new Number(this.discountAmount).valueOf();
        } catch (err) {
          this.showAlert("error", this.$t("message.incorrect_discount_number"));
          return;
        }
      }
      const postData = {
        items: items,
        processedBy: "desk",
        discountPercent: discountPercent,
        discountAmount: discountAmount,
        buyer: this.selectedMember,
        email: this.email,
        paidWith: this.paidWith
      };
      var postUrl =
        "payments/academy/" +
        this.$store.state.managedAcademy +
        "/onetime/cash";
      Api()
        .post(postUrl, postData)
        .then((response) => {
          const data = response.data;
          if (data) {
            if (data.transaction)
              this.navigateTo({ name: 'viewitemstransaction', params: { transactionId: data.transaction } });
            else
              this.navigateTo({
                name: "transactions",
                query: { status: "success" },
              });
          }
        })
        .catch((e) => {
          this.handlingCharges = false;
          console.log(e)
          if (e)
            this.showAlert("error", this.$t("message.error_generic"));
        });
    },
    initCheckout(sendNotification) {
      if (this.handlingCharges)
        return;
      if (this.applyDiscount && this.discountedTotal < 0) {
        this.showAlert("error", this.$t("message.incorrect_discount_number"));
        return;
      }
      this.back = false;
      this.handlingCharges = true;
      const items = this.setupItems();
      let discountPercent = null;
      let discountAmount = null;
      if (this.applyDiscount && this.discountPercent) {
        try {
          discountPercent = new Number(this.discountPercent).valueOf();
        } catch (err) {
          this.showAlert("error", this.$t("message.incorrect_discount_number"));
          return;
        }
      } else if (this.applyDiscount && this.discountAmount) {
        try {
          discountAmount = new Number(this.discountAmount).valueOf();
        } catch (err) {
          this.showAlert("error", this.$t("message.incorrect_discount_number"));
          return;
        }
      }
      const postData = {
        items: items,
        processedBy: sendNotification ? "user" : "desk",
        discountPercent: discountPercent,
        discountAmount: discountAmount,
        buyer: this.selectedMember,
        email: this.email,
        sendNotification: sendNotification,
        saveCreditCard: this.saveCreditCard ? this.saveCreditCard : null,
      };
      if (this.paymentsModule == 'infinicept') {
        postData.currencySymbol = this.currencySymbol;
        postData.total = this.discountedTotal ? this.discountedTotal : this.transactionTotal;
        postData.paymentsModule = 'infinicept';
        postData.routeFrom = 'bulksell';
        this.navigateTo({
          name: "inifnicept-checkout",
          params: { transactionData: postData }
        });
        return;
      } else if (this.paymentsModule == 'easypay') {
        postData.currencySymbol = this.currencySymbol;
        postData.total = this.discountedTotal ? this.discountedTotal : this.transactionTotal;
        postData.paymentsModule = 'easypay';
        postData.routeFrom = 'bulksell';
        this.navigateTo({
          name: "easypay-checkout",
          params: { transactionData: postData }
        });
        return;
      }
      let postUrl =
        "payments/academy/" +
        this.$store.state.managedAcademy +
        "/onetime/credit";
      Api()
        .post(postUrl, postData)
        .then((response) => {
          const data = response.data;
          if (data) {
            if (!sendNotification) {
              if (data.stripeAccountId) {
                this.navigateTo({
                  name: "striperedirect_session",
                  params: { sessionId: data.session },
                  query: { altStripeAccount: data.stripeAccountId }
                });
              } else {
                this.navigateTo({
                  name: "striperedirect_session",
                  params: { sessionId: data.session }
                });
              }
            } else {
              this.handlingCharges = false;
              this.checkoutInitialized = true;
              this.back = true;
              this.clearCart();
              this.navigateTo({
                name: "viewitemstransaction",
                params: { transactionId: data.transaction },
              });
            }
          }
        })
        .catch((e) => {
          this.handlingCharges = false;
          console.log(e)
          if (e)
            this.showAlert("error", this.$t("message.error_generic"));
        });
    },
    getInventoryColor(amount) {
      if (amount == 0) return "red";
      else if (amount > 0 && amount <= 5) return "orange";
      else return "green";
    },
    clearCart() {
      for (const i of this.cart) {
        i.quantity = 1;
      }
      this.cart = [];
      this.automaticTaxes = false;
      this.canUseTerminal = true;
    },
    setupItems(simplified) {
      let items = [];
      for (const i of this.cart) {
        let image = this.userImg(this.academyImage);
        if (i.image) image = this.userImg(i.image);
        if (simplified) {
          items.push({
            quantity: i.quantity,
            product: i._id,
            amount: i.amount,
          });
        } else {
          items.push({
            name: i.name,
            description:
              !i.description || i.description == "" ? " " : i.description,
            images: [image],
            amount: i.amount,
            amountDecimal: i.amountDecimal,
            currency: this.currency,
            quantity: i.quantity,
            product: i._id,
          });
        }
      }
      return items;
    },
    cancel() {
      this.navigateTo({ name: "products" });
    },
  },
};
</script>

<style scoped></style>